import { useAppSelector } from 'careand-redux/store';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import jwtDecode, { JwtPayload } from 'jwt-decode';
import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment-timezone';
import { authActions, AuthState, CareAndToken } from 'careand-redux/slices/auth';
import { tokenExpired } from 'careand-redux/actions/tokenExpired';
import { logout } from 'careand-redux/actions/logout';
import { api } from 'careand-redux/api/ApiThunks';
import { Device } from 'services/Native/Device';
import Bugsnag from '@bugsnag/browser';
import { AdminLoginRequest, Authentication } from 'generated/api';

type Props = {
  version: string;
  closeSocket: (refresh?: boolean) => void;
  openSocket: (token: string) => void;
  onAuthenticationChange: (authenticated: boolean, userId: string | undefined) => void;
};

const AuthenticationController = (props: Props) => {
  const dispatch = useAppDispatch();
  const authentication = useAppSelector((state) => state.authentication);
  const useBiometrics = useAppSelector((state) => !!state.device.useBiometrics);

  const [authenticated, setAuthenticated] = useState<boolean | undefined>(undefined);
  const [storedUserId, setUserId] = useState<string | undefined>(undefined);

  const refreshTokenTimeout = useRef<NodeJS.Timer | undefined>();

  const setAuthenticationToken = (token: string | undefined, refresh = false) => {
    props.closeSocket(refresh);
    if (token && validToken(token)) {
      props.openSocket(token);
    } else {
      if (refreshTokenTimeout.current) {
        clearTimeout(refreshTokenTimeout.current);
      }
      dispatch(authActions.setToken(undefined));
    }
  };

  const setupTokenRefresh = (decodedToken: JwtPayload | undefined) => {
    if (decodedToken) refreshToken(getTimeLeft(decodedToken));
  };

  const refreshToken = (timestamp: number) => {
    if (refreshTokenTimeout.current) {
      clearTimeout(refreshTokenTimeout.current);
    }

    const timeout = Math.max(timestamp, 0);

    refreshTokenTimeout.current = setTimeout(async () => {
      const token = await dispatch(api.Authentication.refreshToken());
      if (token?.token) {
        const decodedToken = jwtDecode<JwtPayload>(token.token);
        const timeLeft = getTimeLeft(decodedToken);
        refreshToken(timeLeft);
      }
    }, timeout);
  };

  const attemptLoginFromDeviceCredentials = async () => {
    if (!Device.isMobile()) {
      return;
    }

    logSucc('[auto-login] from device attempt');
    const activeUserId = await Device.requestActiveUserId();
    if (!activeUserId) {
      logErr('[auto-login] missing activeUserId');
      return;
    }
    logSucc(`[auto-login] ${activeUserId}`);
    const credentials = await Device.requestStoredUserCredentials(activeUserId);
    if (!credentials) {
      logErr(`[auto-login] missing credentials for ${activeUserId}`);
      return;
    }
    const code = (await Device.requestOTPCode({ username: credentials.username }))?.code;

    const auth: Authentication = {
      username: credentials.username,
      password: credentials.password,
      code,
    };

    const loginPayload = await dispatch(
      api.Authentication.userLogin({
        authentication: auth,
        $$extraHeaders: {
          tenant_id: credentials.tenantId,
        },
      }),
    );

    if (!loginPayload) {
      logErr(`[auto-login] login: Failed ${activeUserId}`);
    } else {
      logSucc(`[auto-login] login: Success ${activeUserId}`);
    }

    return loginPayload;
  };

  const verifyAuthentication = async () => {
    let token = authentication.token;
    let decodedToken: CareAndToken | undefined = authentication.decodedToken;

    let prevAuth = authenticated;
    let currentAuth = isAuthenticated(authentication);
    let userId = undefined;
    let reauthenticated = false;

    if (validToken(token)) {
      currentAuth = true;
      userId = authentication.decodedToken.sub;
    }

    if (!validToken(token) || !currentAuth) {
      currentAuth = false;
      userId = undefined;
      decodedToken = undefined;

      const loginPayload = await attemptLoginFromDeviceCredentials();
      const userPayload = loginPayload && loginPayload[loginPayload.role];
      // A payload without a token means 2FA is still required — that must be
      // treated as a failed auto-login, not an authenticated session
      if (userPayload?.token) {
        currentAuth = true;
        reauthenticated = true;
        token = userPayload.token;
        userId = userPayload.user?.id;
        decodedToken = jwtDecode<CareAndToken>(token);
      } else {
        dispatch(tokenExpired());
        setAuthenticationToken(undefined);
      }
    }

    const versionChanged = hasVersionChanged(authentication, props.version);

    if (versionChanged) {
      dispatch(logout());
      currentAuth = false;
    } else if (!authentication.version) {
      dispatch(authActions.setVersionNumber(props.version));
    }

    currentAuth ||= false;

    if (prevAuth !== currentAuth || userId !== storedUserId || reauthenticated) {
      if (currentAuth) {
        // Impersonation (View as Patient) sessions must not open a socket
        // or schedule a refresh — socket rooms would leak patient events to
        // staff, and refresh would strip the read_only claim.
        const isImpersonation = decodedToken?.impersonated_by;
        if (!isImpersonation) {
          setAuthenticationToken(token, true);
          setupTokenRefresh(decodedToken);
        }
      }
    }
    setAuthenticated(currentAuth);
    setUserId(userId);
    props.onAuthenticationChange(currentAuth, userId);
  };

  useEffect(() => {
    verifyAuthentication();
  }, [authentication.decodedToken]);

  useEffect(() => {
    Device.sendWebAppInfo({
      isLoggedIn: authentication.loggedIn,
      biometricAuthentication: useBiometrics,
    });
  }, [authentication.loggedIn, useBiometrics]);

  return null;
};

function hasVersionChanged(auth: AuthState, version: string) {
  return auth.version && version !== auth.version;
}

function isAuthenticated(auth: AuthState) {
  return auth.loggedIn === true && validToken(auth.token);
}

function validToken(token: string | undefined): boolean {
  if (!token) return false;
  let decodedToken = jwtDecode<JwtPayload>(token);
  return !!decodedToken.exp && decodedToken.exp >= moment().unix();
}

function getTimeLeft(decodedToken: JwtPayload) {
  if (!decodedToken.exp) {
    return 0;
  }
  return moment.unix(decodedToken.exp).diff(moment()) / 2;
}

function logErr(...args: any[]) {
  console.error(args);
  const msg = args.map((it) => (typeof it === 'string' ? it : JSON.stringify(it))).join(' ');
  Bugsnag.notify(msg);
}

function logSucc(...args: any[]) {
  console.log(...args);
}

export default AuthenticationController;
